import { useParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ArrowLeft, Mail, Phone, MapPin, Building, Users, Briefcase } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Spinner } from '@/components/ui/spinner'
import { StatusBadge } from '@/components/StatusBadge'
import { useCustomer, useCustomerReferrals } from '@/hooks/useCustomers'
import { useProjects } from '@/hooks/useProjects'
import { LeadStatus, ProjectStatus } from '@/types/api'

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: customer, isLoading: customerLoading } = useCustomer(id!)
  const { data: referrals, isLoading: referralsLoading } = useCustomerReferrals(id!)

  // Fetch won projects for this customer's lead
  const { data: wonProjects } = useProjects(
    customer ? { lead_id: customer.lead_id, project_status: ProjectStatus.WON } : undefined
  )

  if (customerLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!customer) {
    return (
      <div className="flex h-64 flex-col items-center justify-center">
        <p className="text-lg text-muted-foreground">Customer not found</p>
        <Button variant="link" onClick={() => navigate('/customers')}>
          Back to Customers
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/customers')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {customer.first_name} {customer.last_name}
          </h1>
          <p className="text-sm text-muted-foreground">
            Customer since {format(new Date(customer.converted_at), 'MMMM d, yyyy')}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Customer Info */}
        <Card>
          <CardHeader>
            <CardTitle>Customer Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {customer.email && (
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>{customer.email}</span>
              </div>
            )}
            {customer.phone && (
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{customer.phone}</span>
              </div>
            )}
            {(customer.address || customer.city || customer.zip) && (
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>
                  {[customer.address, customer.city, customer.zip]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </div>
            )}
            {customer.company_name && (
              <div className="flex items-center gap-3">
                <Building className="h-4 w-4 text-muted-foreground" />
                <span>{customer.company_name}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Referral Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Referral Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <div>
                <p className="text-3xl font-bold">{customer.referral_count}</p>
                <p className="text-muted-foreground">
                  {customer.referral_count === 1 ? 'Referral' : 'Referrals'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Won Projects */}
      {wonProjects && wonProjects.items.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Won Projects
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead className="hidden md:table-cell">Size</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead className="hidden md:table-cell">Won Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wonProjects.items.map((project) => (
                  <TableRow key={project.id}>
                    <TableCell className="font-medium">
                      {[project.address, project.city].filter(Boolean).join(', ') || '-'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell capitalize">
                      {project.project_size || '-'}
                    </TableCell>
                    <TableCell>
                      {project.project_cost != null
                        ? `$${project.project_cost.toFixed(2)}`
                        : '-'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">
                      {format(new Date(project.updated_at), 'MMM d, yyyy')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Referrals Table */}
      <Card>
        <CardHeader>
          <CardTitle>Referred Leads</CardTitle>
        </CardHeader>
        <CardContent>
          {referralsLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Referred On</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals?.referrals.map((referral) => (
                  <TableRow key={referral.lead_id}>
                    <TableCell className="font-medium">
                      {referral.lead_name}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={referral.lead_status as LeadStatus} />
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">
                      {format(new Date(referral.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/leads/${referral.lead_id}`)}
                      >
                        View Lead
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(!referrals?.referrals || referrals.referrals.length === 0) && (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="text-center text-muted-foreground"
                    >
                      No referrals found for this customer
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
